import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  navigateToPreviousVersion,
  navigateToNextVersion,
  getVersionNavigationInfo,
  isViewingHistoricalVersion,
  undo,
  redo,
  canUndo,
  canRedo,
} from '../db';
import { VersionNavigationInfo } from '../types/versioning';
import { BUDGET_DATA_UPDATED_EVENT } from '../constants/events';

/**
 * Hook for version navigation functionality
 */
export function useVersionNavigation(budgetId: number) {
  const queryClient = useQueryClient();

  // Get version navigation info
  const { data: versionInfo = {
    canGoBack: false,
    canGoForward: false,
    currentVersionIndex: 0,
    totalVersions: 0,
  } } = useQuery<VersionNavigationInfo>({
    queryKey: ['versionInfo', budgetId],
    queryFn: () => getVersionNavigationInfo(budgetId),
    staleTime: 0, // Always fresh
  });

  // Check if viewing historical version
  const { data: isHistorical = false } = useQuery<boolean>({
    queryKey: ['isHistoricalVersion', budgetId],
    queryFn: () => isViewingHistoricalVersion(budgetId),
    staleTime: 0, // Always fresh
  });

  // Check undo/redo availability reactively
  const { data: canUndoData = false } = useQuery<boolean>({
    queryKey: ['canUndo', budgetId],
    queryFn: () => canUndo(budgetId),
    staleTime: 0, // Always fresh
  });

  const { data: canRedoData = false } = useQuery<boolean>({
    queryKey: ['canRedo', budgetId],
    queryFn: () => canRedo(budgetId),
    staleTime: 0, // Always fresh
  });

  // Navigate to previous version
  const goBackMutation = useMutation({
    mutationFn: () => {
      const result = navigateToPreviousVersion(budgetId);
      return Promise.resolve(result);
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['budget', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['versionInfo', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['isHistoricalVersion', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['canUndo', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['canRedo', budgetId] });
    },
  });

  // Navigate to next version
  const goForwardMutation = useMutation({
    mutationFn: () => {
      const result = navigateToNextVersion(budgetId);
      return Promise.resolve(result);
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['budget', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['versionInfo', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['isHistoricalVersion', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['canUndo', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['canRedo', budgetId] });
    },
  });

  // Undo mutation
  const undoMutation = useMutation({
    mutationFn: () => {
      const result = undo(budgetId);
      return Promise.resolve(result);
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['budget', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['versionInfo', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['isHistoricalVersion', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['canUndo', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['canRedo', budgetId] });
    },
  });

  // Redo mutation
  const redoMutation = useMutation({
    mutationFn: () => {
      const result = redo(budgetId);
      return Promise.resolve(result);
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['budget', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['versionInfo', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['isHistoricalVersion', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['canUndo', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['canRedo', budgetId] });
    },
  });

  // Listen for budget data updates to refresh version info
  useEffect(() => {
    const handleDataUpdated = () => {
      queryClient.invalidateQueries({ queryKey: ['versionInfo', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['isHistoricalVersion', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['canUndo', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['canRedo', budgetId] });
    };

    window.addEventListener(BUDGET_DATA_UPDATED_EVENT, handleDataUpdated);
    return () => {
      window.removeEventListener(BUDGET_DATA_UPDATED_EVENT, handleDataUpdated);
    };
  }, [budgetId, queryClient]);

  return {
    versionInfo,
    isHistorical,
    goBack: () => goBackMutation.mutate(),
    goForward: () => goForwardMutation.mutate(),
    undo: () => undoMutation.mutate(),
    redo: () => redoMutation.mutate(),
    canGoBack: versionInfo.canGoBack,
    canGoForward: versionInfo.canGoForward,
    canUndo: canUndoData,
    canRedo: canRedoData,
    isNavigating: goBackMutation.isPending || goForwardMutation.isPending || undoMutation.isPending || redoMutation.isPending,
  };
}

/**
 * Hook for keyboard shortcuts
 */
export function useVersionKeyboardShortcuts(budgetId: number) {
  const { undo, redo, canUndo, canRedo } = useVersionNavigation(budgetId);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle shortcuts if Ctrl/Cmd is pressed
      if (!(event.ctrlKey || event.metaKey)) return;

      // Check if we're in an input field
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      switch (event.key) {
        case 'z':
        case 'Z':
          if (event.shiftKey) {
            // Ctrl+Shift+Z = Redo (alternative to Ctrl+Y)
            if (canRedo) {
              event.preventDefault();
              redo();
            }
          } else {
            // Ctrl+Z = Undo
            if (canUndo) {
              event.preventDefault();
              undo();
            }
          }
          break;
        case 'y':
        case 'Y':
          // Ctrl+Y = Redo
          if (canRedo) {
            event.preventDefault();
            redo();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [undo, redo, canUndo, canRedo]);
}